import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Department, DepartmentFormData } from '../models/DepartmentModel'
import * as DepartmentCRUD from './DepartmentCRUD'

const normalizeDepartmentsPayload = (payload: unknown): Department[] => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown }).results)) {
    return (payload as { results: Department[] }).results
  }

  return []
}

// Async thunks
export const fetchDepartments = createAsyncThunk(
  'department/fetchDepartments',
  async () => {
    const response = await DepartmentCRUD.getDepartments()
    return normalizeDepartmentsPayload(response.data)
  }
)

export const createDepartment = createAsyncThunk(
  'department/createDepartment',
  async (departmentData: DepartmentFormData) => {
    const response = await DepartmentCRUD.createDepartment(departmentData)
    return response.data
  }
)

export const updateDepartment = createAsyncThunk(
  'department/updateDepartment',
  async ({ id, departmentData }: { id: string; departmentData: DepartmentFormData }) => {
    const response = await DepartmentCRUD.updateDepartment(id, departmentData)
    return response.data
  }
)

export const deleteDepartment = createAsyncThunk(
  'department/deleteDepartment',
  async (id: string) => {
    await DepartmentCRUD.deleteDepartment(id)
    return id
  }
)

export const fetchDepartmentsByCompany = createAsyncThunk(
  'department/fetchDepartmentsByCompany',
  async (companyId: string) => {
    const response = await DepartmentCRUD.getDepartmentsByCompany(companyId)
    return response.data
  }
)

export const fetchRootDepartments = createAsyncThunk(
  'department/fetchRootDepartments',
  async () => {
    const response = await DepartmentCRUD.getRootDepartments()
    return response.data
  }
)

export const fetchDepartmentHierarchy = createAsyncThunk(
  'department/fetchDepartmentHierarchy',
  async () => {
    const response = await DepartmentCRUD.getDepartmentHierarchy()
    return response.data
  }
)

// State interface
interface DepartmentState {
  departments: Department[]
  loading: boolean
  error: string | null
  successMessage: string | null
  selectedDepartment: Department | null
}

// Initial state
const initialState: DepartmentState = {
  departments: [],
  loading: false,
  error: null,
  successMessage: null,
  selectedDepartment: null,
}

// Slice
const departmentSlice = createSlice({
  name: 'department',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null
    },
    setSelectedDepartment: (state, action: PayloadAction<Department | null>) => {
      state.selectedDepartment = action.payload
    },
    clearSelectedDepartment: (state) => {
      state.selectedDepartment = null
    },
    clearRequestLoading: (state) => {
      state.loading = false
    },
  },
  extraReducers: (builder) => {
    // Fetch departments
    builder
      .addCase(fetchDepartments.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.loading = false
        state.departments = normalizeDepartmentsPayload(action.payload)
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch departments'
      })

    // Create department
    builder
      .addCase(createDepartment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.loading = false
        state.departments.push(action.payload)
        state.successMessage = 'Department created successfully'
      })
      .addCase(createDepartment.rejected, (state) => {
        state.loading = false
      })

    // Update department
    builder
      .addCase(updateDepartment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        state.loading = false
        const index = state.departments.findIndex(dept => dept.id === action.payload.id)
        if (index !== -1) {
          state.departments[index] = action.payload
        }
        state.successMessage = 'Department updated successfully'
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update department'
      })

    // Delete department
    builder
      .addCase(deleteDepartment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.loading = false
        state.departments = state.departments.filter(dept => dept.id !== action.payload)
        state.successMessage = 'Department deleted successfully'
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete department'
      })

    // Fetch departments by company
    builder
      .addCase(fetchDepartmentsByCompany.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDepartmentsByCompany.fulfilled, (state, action) => {
        state.loading = false
        state.departments = action.payload
      })
      .addCase(fetchDepartmentsByCompany.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch departments by company'
      })

    // Fetch root departments
    builder
      .addCase(fetchRootDepartments.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRootDepartments.fulfilled, (state, action) => {
        state.loading = false
        state.departments = action.payload
      })
      .addCase(fetchRootDepartments.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch root departments'
      })

    // Fetch department hierarchy
    builder
      .addCase(fetchDepartmentHierarchy.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDepartmentHierarchy.fulfilled, (state, action) => {
        state.loading = false
        state.departments = action.payload
      })
      .addCase(fetchDepartmentHierarchy.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch department hierarchy'
      })
  },
})

// Export actions
export const {
  clearError,
  clearSuccessMessage,
  setSelectedDepartment,
  clearSelectedDepartment,
  clearRequestLoading,
} = departmentSlice.actions

// Export selectors
export const selectDepartments = (state: { department: DepartmentState }) => normalizeDepartmentsPayload(state.department.departments)
export const selectDepartmentLoading = (state: { department: DepartmentState }) => state.department.loading
export const selectDepartmentError = (state: { department: DepartmentState }) => state.department.error
export const selectDepartmentSuccessMessage = (state: { department: DepartmentState }) => state.department.successMessage
export const selectSelectedDepartment = (state: { department: DepartmentState }) => state.department.selectedDepartment

// Export reducer
export default departmentSlice.reducer 